/**
 * Centralized KPI calculations — Single source of truth
 *
 * Used by:
 * - useDashboardEnrichedQuery (company dashboard)
 * - useSiteBudgetAnalysis (site budget page)
 * - SiteKPIs (display component)
 *
 * BUSINESS FORMULAS:
 * - theoreticalVolume = plannedVolume * (progress_percentage / 100)
 * - progress = (theoreticalVolume / plannedVolume) * 100
 * - volumeVariance = theoreticalVolume - deliveredVolume
 * - If variance > 0 → saving (success/green)
 * - If variance < 0 → loss (destructive/red)
 */

// ===== TYPES =====

export interface WorkItemData {
  id: string;
  volume: number;
  site_id?: string;
}

export interface ProgressData {
  work_item_id: string;
  progress_percentage: number;
}

export interface PourWorkItemData {
  work_item_id: string;
  volume_poured: number;
  actual_volume_poured: number | null;
  site_id?: string;
}

export interface DeliveryWorkItemData {
  work_item_id: string;
  volume_delivered: number;
}

export interface FormulaData {
  volume: number;
  unit_price_m3: number;
  site_id?: string;
}

export interface SupplierOptionData {
  category: string;
  unit_type: string;
  price: number;
  active?: boolean;
  site_id?: string;
}

export interface VolumeKPIs {
  volumePlanned: number;
  volumeTheoretical: number;
  volumeDelivered: number;
  volumeVariance: number;
  volumeVariancePercent: number;
  progress: number;
}

export interface SiteVolumeKPIs extends VolumeKPIs {
  siteId: string;
}

export interface BudgetKPIs {
  budgetFormulas: number;
  budgetTransport: number;
  budgetTaxes: number;
  budgetTotal: number;
}

export interface CompanyKPIs {
  totalVolumeDelivered: number;
  totalVolumePlanned: number;
  totalVolumeTheoretical: number;
  averageProgress: number;
  budgetTotal: number;
  siteKPIs: Map<string, SiteVolumeKPIs>;
}

// ===== UNIT VOLUME CALCULATIONS (per work item) =====

/**
 * Calculate theoretical volume for a work item
 * theoreticalVolume = plannedVolume * (progress_percentage / 100)
 */
export function calculateTheoreticalVolume(
  plannedVolume: number,
  progressPercentage: number
): number {
  if (progressPercentage === 0) return 0;
  return plannedVolume * (progressPercentage / 100);
}

/**
 * Calculate volume variance
 * volumeVariance = theoreticalVolume - deliveredVolume
 * Positive = saving, Negative = loss
 */
export function calculateVolumeVariance(
  theoreticalVolume: number,
  deliveredVolume: number
): number {
  return theoreticalVolume - deliveredVolume;
}

/**
 * Calculate variance percentage
 */
export function calculateVariancePercent(
  volumeVariance: number,
  theoreticalVolume: number
): number {
  if (theoreticalVolume === 0) return 0;
  return (volumeVariance / theoreticalVolume) * 100;
}

/**
 * Calculate progress (percentage)
 * progress = (theoreticalVolume / plannedVolume) * 100
 */
export function calculateProgress(
  theoreticalVolume: number,
  plannedVolume: number
): number {
  if (plannedVolume === 0) return 0;
  return (theoreticalVolume / plannedVolume) * 100;
}

// ===== AGGREGATE VOLUME CALCULATIONS (per site or global) =====

/**
 * Create a map of progress percentages by work_item_id
 */
export function buildProgressMap(
  progressData: ProgressData[]
): Map<string, number> {
  return new Map(
    progressData.map(wp => [wp.work_item_id, wp.progress_percentage])
  );
}

/**
 * Calculate planned volume by site
 */
export function buildPlannedVolumeBySite(
  workItems: WorkItemData[]
): Map<string, number> {
  const map = new Map<string, number>();
  workItems.forEach(item => {
    if (!item.site_id) return;
    const current = map.get(item.site_id) || 0;
    map.set(item.site_id, current + Number(item.volume || 0));
  });
  return map;
}

/**
 * Calculate theoretical volume by site
 * theoreticalVolume = Σ(volume × progress_percentage / 100)
 */
export function buildTheoreticalVolumeBySite(
  workItems: WorkItemData[],
  progressMap: Map<string, number>
): Map<string, number> {
  const map = new Map<string, number>();
  workItems.forEach(item => {
    if (!item.site_id) return;
    const progressPct = progressMap.get(item.id) || 0;
    const theoretical = Number(item.volume || 0) * progressPct / 100;
    const current = map.get(item.site_id) || 0;
    map.set(item.site_id, current + theoretical);
  });
  return map;
}

/**
 * Determine volume to use based on priority:
 * 1. actual_volume_poured if explicitly different
 * 2. volume_poured otherwise
 */
export function getVolumeFromPourWorkItem(
  pourItem: PourWorkItemData
): number {
  const volumeToUse = pourItem.actual_volume_poured !== null
    ? pourItem.actual_volume_poured
    : pourItem.volume_poured;
  return Number(volumeToUse || 0);
}

/**
 * Calculate delivered volume by site from pour_work_items
 * Priority: actual_volume_poured > volume_poured
 */
export function buildDeliveredVolumeBySite(
  pourWorkItems: PourWorkItemData[]
): Map<string, number> {
  const map = new Map<string, number>();
  pourWorkItems.forEach(pwi => {
    const siteId = pwi.site_id;
    if (!siteId) return;

    const volumeToUse = getVolumeFromPourWorkItem(pwi);
    const current = map.get(siteId) || 0;
    map.set(siteId, current + volumeToUse);
  });
  return map;
}

/**
 * Calculate delivered volume by work_item_id
 * Source: pour_work_items (filtered upstream on status in_progress/completed)
 * Priority: actual_volume_poured (manual reallocation) > volume_poured (ordered)
 */
export function buildDeliveredVolumeByWorkItem(
  pourWorkItems: Array<{ work_item_id: string; volume_poured: number; actual_volume_poured: number | null }>,
  _deliveryWorkItems?: DeliveryWorkItemData[]
): Map<string, number> {
  const result = new Map<string, number>();
  pourWorkItems.forEach(pwi => {
    const volumeToUse = pwi.actual_volume_poured !== null
      ? pwi.actual_volume_poured
      : pwi.volume_poured;
    const current = result.get(pwi.work_item_id) || 0;
    result.set(pwi.work_item_id, current + Number(volumeToUse || 0));
  });
  return result;
}

/**
 * Calculate all volume KPIs for a site
 */
export function calculateSiteVolumeKPIs(
  workItems: WorkItemData[],
  progressMap: Map<string, number>,
  deliveredVolumeMap: Map<string, number>
): VolumeKPIs {
  let volumePlanned = 0;
  let volumeTheoretical = 0;
  let volumeDelivered = 0;

  workItems.forEach(item => {
    const vol = Number(item.volume || 0);
    const progressPct = progressMap.get(item.id) || 0;
    const volTheo = calculateTheoreticalVolume(vol, progressPct);
    const volDelivered = deliveredVolumeMap.get(item.id) || 0;

    volumePlanned += vol;
    volumeTheoretical += volTheo;
    volumeDelivered += volDelivered;
  });

  const volumeVariance = calculateVolumeVariance(volumeTheoretical, volumeDelivered);
  const volumeVariancePercent = calculateVariancePercent(volumeVariance, volumeTheoretical);
  const progress = calculateProgress(volumeTheoretical, volumePlanned);

  return {
    volumePlanned,
    volumeTheoretical,
    volumeDelivered,
    volumeVariance,
    volumeVariancePercent,
    progress,
  };
}

// ===== BUDGET CALCULATIONS =====

/**
 * Calculate concrete formula budget
 * budgetFormulas = Σ(volume × unit_price_m3)
 */
export function calculateFormulaBudget(formulas: FormulaData[]): number {
  return formulas.reduce(
    (sum, f) => sum + Number(f.volume) * Number(f.unit_price_m3),
    0
  );
}

/**
 * Calculate theoretical transport budget
 * transport = (totalVolume / 7.5) × flat_rate_price
 */
export function calculateBudgetTransport(
  volumeTotal: number,
  options: SupplierOptionData[]
): number {
  const transportOption = options.find(
    o => o.category === 'transport' && o.unit_type === 'forfait'
  );
  if (!transportOption) return 0;
  return (volumeTotal / 7.5) * transportOption.price;
}

/**
 * Calculate taxes budget
 * taxes = Σ(euro_m3 × totalVolume)
 */
export function calculateBudgetTaxes(
  volumeTotal: number,
  options: SupplierOptionData[]
): number {
  return options
    .filter(o => o.category === 'taxes' && o.unit_type === 'euro_m3')
    .reduce((sum, o) => sum + o.price * volumeTotal, 0);
}

/**
 * Calculate all budget KPIs for a site
 */
export function calculateSiteBudgetKPIs(
  formulas: FormulaData[],
  options: SupplierOptionData[]
): BudgetKPIs {
  const budgetFormulas = calculateFormulaBudget(formulas);
  const volumeTotal = formulas.reduce((sum, f) => sum + Number(f.volume), 0);
  const budgetTransport = calculateBudgetTransport(volumeTotal, options);
  const budgetTaxes = calculateBudgetTaxes(volumeTotal, options);

  return {
    budgetFormulas,
    budgetTransport,
    budgetTaxes,
    budgetTotal: budgetFormulas + budgetTransport + budgetTaxes,
  };
}

// ===== COMPANY CALCULATIONS (multi-site aggregates) =====

/**
 * Calculate aggregated KPIs for all company sites
 */
export function calculateCompanyKPIs(
  siteIds: string[],
  workItems: WorkItemData[],
  progressData: ProgressData[],
  pourWorkItems: PourWorkItemData[],
  formulas: FormulaData[],
  options: SupplierOptionData[]
): CompanyKPIs {
  // Build maps
  const progressMap = buildProgressMap(progressData);
  const plannedBySite = buildPlannedVolumeBySite(workItems);
  const theoreticalBySite = buildTheoreticalVolumeBySite(workItems, progressMap);
  const deliveredBySite = buildDeliveredVolumeBySite(pourWorkItems);

  // Calculate per-site KPIs
  const siteKPIs = new Map<string, SiteVolumeKPIs>();
  const progressList: number[] = [];

  siteIds.forEach(siteId => {
    const volumePlanned = plannedBySite.get(siteId) || 0;
    const volumeTheoretical = theoreticalBySite.get(siteId) || 0;
    const volumeDelivered = deliveredBySite.get(siteId) || 0;
    const volumeVariance = calculateVolumeVariance(volumeTheoretical, volumeDelivered);
    const volumeVariancePercent = calculateVariancePercent(volumeVariance, volumeTheoretical);
    const siteProgress = calculateProgress(volumeTheoretical, volumePlanned);

    siteKPIs.set(siteId, {
      siteId,
      volumePlanned,
      volumeTheoretical,
      volumeDelivered,
      volumeVariance,
      volumeVariancePercent,
      progress: siteProgress,
    });

    if (volumePlanned > 0) {
      progressList.push(siteProgress);
    }
  });

  // Aggregate totals
  const totalVolumeDelivered = Array.from(deliveredBySite.values()).reduce((s, v) => s + v, 0);
  const totalVolumePlanned = Array.from(plannedBySite.values()).reduce((s, v) => s + v, 0);
  const totalVolumeTheoretical = Array.from(theoreticalBySite.values()).reduce((s, v) => s + v, 0);

  // Average progress (mean of progress per site)
  const averageProgress = progressList.length > 0
    ? progressList.reduce((s, a) => s + a, 0) / progressList.length
    : 0;

  // Total budget
  let budgetTotal = 0;
  siteIds.forEach(siteId => {
    const siteFormulas = formulas.filter(f => f.site_id === siteId);
    const siteOptions = options.filter(o => o.site_id === siteId);
    const siteBudget = calculateSiteBudgetKPIs(siteFormulas, siteOptions);
    budgetTotal += siteBudget.budgetTotal;
  });

  return {
    totalVolumeDelivered,
    totalVolumePlanned,
    totalVolumeTheoretical,
    averageProgress,
    budgetTotal,
    siteKPIs,
  };
}

// ===== ADDITIONAL EXPORTS =====

/**
 * Calculate weighted progress (alternative)
 * Formula: (Σ theoreticalVolume / Σ plannedVolume) × 100
 */
export function calculateWeightedProgress(
  totalVolumeTheoretical: number,
  totalVolumePlanned: number
): number {
  if (totalVolumePlanned === 0) return 0;
  return (totalVolumeTheoretical / totalVolumePlanned) * 100;
}
